import React from 'react'
import * as yup from 'yup'
import {
  Box,
  Checkbox,
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import {
  Business as BusinessIcon,
  EmailOutlined as EmailOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material'
import { useTranslation } from 'next-i18next'
import { useFormik } from 'formik'
import FlatTextField from '@components/FlatTextField'
import PasswordTextField from '@imports/PasswordTextField'
import NextLink from '@imports/NextLink'
import FlatButton from '@components/FlatButton'
import { useRegister } from '@api/auth'
import moment from 'moment'
import { toast } from 'react-toastify'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import CreateAccountLayout from './CreateAccountLayout'
import Loader from './Loader'

const validationSchema = yup.object().shape({
  domain: yup.string().subdomain().required(),
  email: yup.string().email().required(),
  password: yup.string().min(6).required(),
  terms: yup.boolean().required().oneOf([true], 'The terms and conditions must be accepted.'),
})

function CreateAccountSection({ handleNext, setData }) {
  const { t, i18n } = useTranslation()
  const direction = i18n.dir(i18n.language)

  const registerMutation = useRegister()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const formik = useFormik({
    initialValues: {
      domain: '',
      email: '',
      password: '',
      terms: false,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (executeRecaptcha)
        executeRecaptcha('')
          .then((captchaToken) => {
            registerMutation
              .mutateAsync({ ...values, 'g-recaptcha-response': captchaToken })
              .then((res) => {
                setData((prevData) => ({
                  ...prevData,
                  token: res.data.data.access_token,
                  domain:
                    (process.env.NEXT_PUBLIC_ENV === 'development' ? 'dev-' : '') + values.domain,
                  account_creation_time: moment(),
                }))
                handleNext()
              })
              .catch((err) => {
                if (err.response) {
                  err.response?.data?.data?.errors?.map((error) => toast.error(error))
                } else toast.error('unknown error')
              })
              .finally(() => actions.setSubmitting(false))
          })
          .catch(() => {
            toast.error('Failed to generate token')
            actions.setSubmitting(false)
          })
      else {
        toast.error('Failed to generate token')
        actions.setSubmitting(false)
      }
    },
  })

  return (
    <>
      <Fade
        in={!formik.isSubmitting}
        sx={{ display: !formik.isSubmitting ? '' : 'none' }}
      >
        <Box>
          <CreateAccountLayout subtitle={t('register:Sign_up_with_your_email')}>
            <Box mt={6}>
              <Grid
                container
                spacing={5}
                component="form"
                onSubmit={formik.handleSubmit}
              >
                <Grid
                  item
                  xs={12}
                >
                  <FlatTextField
                    name="domain"
                    value={formik.values.domain}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.domain && Boolean(formik.errors.domain)}
                    helperText={formik.touched.domain && formik.errors.domain}
                    fullWidth
                    label={t('common:Institution_name')}
                    backgroundColor="neutral.main"
                    size="large"
                    StartIcon={BusinessIcon}
                    inputProps={{ 'aria-label': t('common:Institution_name') }}
                    // eslint-disable-next-line react/jsx-no-duplicate-props
                    InputProps={{
                      style: { direction: 'ltr' },
                      endAdornment: (
                        <InputAdornment position={direction === 'rtl' ? 'start' : 'end'}>
                          <TextField
                            size="large"
                            select
                            inputProps={{
                              sx: { fontSize: { xs: 13, md: 'inherit' } },
                              'aria-label': t('common:Domain'),
                            }}
                            sx={{
                              width: { xs: 125, sm: 225, md: 200, lg: 250 },
                              border: 1,
                              ml: direction === 'rtl' ? -1.75 : 0,
                              mr: direction === 'ltr' ? -1.75 : 0,
                              borderTopLeftRadius: direction === 'rtl' ? 8 : 0,
                              borderBottomLeftRadius: direction === 'rtl' ? 8 : 0,
                              borderTopRightRadius: direction === 'ltr' ? 8 : 0,
                              borderBottomRightRadius: direction === 'ltr' ? 8 : 0,
                              borderColor: 'primary.main',
                            }}
                            defaultValue="blueassembly"
                          >
                            {[
                              {
                                value: 'blueassembly',
                                label: '.blueassembly.com',
                              },
                            ].map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                style={{ direction: 'ltr' }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                >
                  <FlatTextField
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    fullWidth
                    label={t('common:Email')}
                    type="email"
                    backgroundColor="neutral.main"
                    size="large"
                    StartIcon={EmailOutlinedIcon}
                    inputProps={{ 'aria-label': t('common:Email') }}
                    // eslint-disable-next-line react/jsx-no-duplicate-props
                    InputProps={{ style: { direction: 'ltr' } }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                >
                  <PasswordTextField
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    fullWidth
                    label={t('common:Password')}
                    backgroundColor="neutral.main"
                    size="large"
                    StartIcon={LockOutlinedIcon}
                    inputProps={{ 'aria-label': t('common:Password') }}
                  />
                  <FormControl
                    sx={{ mt: 1 }}
                    component="fieldset"
                    variant="standard"
                    error={formik.touched.terms && Boolean(formik.errors.terms)}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.terms}
                          onChange={(event) => formik.setFieldValue('terms', event.target.checked)}
                          name="terms"
                        />
                      }
                      label={
                        <Typography
                          variant="body1"
                          fontWeight={400}
                          sx={{ fontSize: { xs: 14, md: 'inherit' } }}
                          textTransform="capitalize"
                        >
                          {t('register:accept_our')}{' '}
                          <Link
                            href="/terms-and-conditions"
                            target="_blank"
                            aria-label={t('register:terms_&_conditions')}
                          >
                            {t('register:terms_&_conditions')}
                          </Link>
                        </Typography>
                      }
                    />
                    <FormHelperText>{formik.touched.terms && formik.errors.terms}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={12}
                >
                  <FlatButton
                    id="create-account-button"
                    variant="contained"
                    sx={{ mb: 2 }}
                    onClick={formik.handleSubmit}
                    aria-label={t('register:create_account')}
                    type="submit"
                    isLoading={formik.isSubmitting}
                  >
                    {t('register:create_account')}
                  </FlatButton>
                  <Typography
                    variant="body1"
                    fontWeight={400}
                    color="neutral.dark"
                    sx={{ fontSize: { xs: 14, md: 'inherit' } }}
                    display="block"
                  >
                    <NextLink href="/login">
                      {t('register:Already_have_an_account')}{' '}
                      <Typography
                        variant="body1"
                        component="span"
                        fontWeight={600}
                        color="#000"
                        fontSize="inherit"
                      >
                        {t('common:login')}
                      </Typography>
                    </NextLink>
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </CreateAccountLayout>
        </Box>
      </Fade>
      <Fade
        in={formik.isSubmitting}
        sx={{ display: formik.isSubmitting ? '' : 'none' }}
      >
        <Box>
          <Loader active={formik.isSubmitting} />
        </Box>
      </Fade>
    </>
  )
}

export default CreateAccountSection
